import { NgModule } from '@angular/core';
import { RouterModule, Routes, UrlSerializer } from '@angular/router';
import { authGuard } from '@core/security/guards/auth.guard';
import { subscriptionGuard } from '@core/security/guards/subscription.guard';
import { SkeletonComponent } from '@layout/skeleton/skeleton.component';
import { PublicChromeLayoutComponent } from '@layout/public-chrome-layout/public-chrome-layout.component';
import { PublicLayoutComponent } from '@layout/public-layout/public-layout.component';
import { renderStaticHtmlGuard } from '@modules/static-content/guards/static-content.guard';
import { OfertaLaboralUrlSerializer } from '@modules/public/oferta-laboral/oferta-laboral-url-serializer';
import { REPORTES_ENTRY_ROUTE } from '@modules/reportes/reportes.entry';

export const routes: Routes = [
  { path: '', redirectTo: 'reclutador', pathMatch: 'full' },
  // entrevista/:token is registered as its own top-level route — NOT nested
  // under PublicLayoutComponent below — so it never inherits that layout's
  // light `<header>`/`<footer>` chrome. That header's `<a href="/">` is the
  // first focusable element on the page; the entrevista page's own `fixed
  // inset-0` immersive shell only visually covers it, it does not remove it
  // from the DOM/tab order. A keyboard candidate tabbing onto that invisible
  // link and activating it would trigger a full non-Angular navigation to
  // `/`, destroying the live MediaStream mid-recording (unrecoverable, since
  // ADR-D11 removed re-record). Must stay BEFORE the PublicLayoutComponent
  // route below so route matching reaches it first.
  {
    path: 'entrevista/:token',
    loadComponent: () =>
      import('@modules/public/entrevista/pages/entrevista/entrevista.page').then(
        (m) => m.EntrevistaPage,
      ),
  },
  {
    path: '',
    component: PublicLayoutComponent,
    children: [
      {
        path: 'invitacion/:token',
        loadComponent: () =>
          import('@modules/public/invitacion/pages/invitacion/invitacion.page').then(
            (m) => m.InvitacionPage,
          ),
      },
      {
        path: 'agendar/:token',
        loadComponent: () =>
          import('@modules/public/agendar/pages/agendar/agendar.page').then(
            (m) => m.AgendarPage,
          ),
      },
      {
        path: 'documentos/:token',
        loadComponent: () =>
          import('@modules/public/documentos/pages/documentos/documentos.page').then(
            (m) => m.DocumentosPage,
          ),
      },
      {
        path: 'reclutador/confirmar-email/:code',
        loadComponent: () =>
          import('@modules/public/confirmar-email/confirmar-email.page').then(
            (m) => m.ConfirmarEmailPage,
          ),
      },
    ],
  },
  // Public job routes live in their own chrome: no authenticated navigation, no guards, and
  // no link to the recruiter login (REQ-RPV-060).
  {
    path: '',
    component: PublicChromeLayoutComponent,
    children: [
      {
        path: 'oferta-laboral/:slug',
        loadComponent: () =>
          import('@modules/public/oferta-laboral/pages/oferta-laboral.page').then(
            (m) => m.OfertaLaboralPage,
          ),
      },
      {
        // The token stays in the URL on purpose: no replaceUrl, so a reload keeps working
        // (decision Engram #2320). Leaks are closed by the referrer policy and PostHog redaction.
        path: 'postulacion/:token',
        loadComponent: () =>
          import('@modules/public/postulacion/pages/postulacion.page').then((m) => m.PostulacionPageComponent),
      },
    ],
  },
  {
    path: '',
    component: SkeletonComponent,
    children: [
      {
        path: 'info/:slug',
        canActivate: [renderStaticHtmlGuard],
        loadComponent: () =>
          import('@modules/static-content/pages/static-content.page').then(
            (m) => m.StaticContentPage,
          ),
      },
    ],
  },
  {
    path: 'empresa',
    loadChildren: () =>
      import('@modules/empresa-publica/empresa-publica.module').then(
        (m) => m.EmpresaPublicaModule
      ),
  },
  {
    path: 'reclutador',
    loadChildren: () => import('@modules/landing/landing.module').then((m) => m.LandingModule),
  },
  {
    path: 'reclutador',
    loadChildren: () => import('@modules/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    path: 'reclutador',
    component: SkeletonComponent,
    canActivate: [authGuard],
    children: [
      // REQ-RSENT-070 / design D-10 — URL legada: `modules/onboarding` queda inalcanzable desde
      // aquí (su borrado es F5/PR-B); cualquier subpaso resuelve por la misma decisión de ruteo
      // post-autenticación que cualquier otra entrada (subscriptionGuard en `vacantes`).
      //
      // post-verify fix (C1): un `redirectTo` con `pathMatch: 'prefix'` NO absorbe los segmentos
      // sobrantes — Angular los CONCATENA al destino (`expandSegmentAgainstRouteUsingRedirect`,
      // `@angular/router/fesm2022/router.mjs`). `{redirectTo:'vacantes', pathMatch:'prefix'}`
      // mandaba `/reclutador/onboarding/paso-1` a `/reclutador/vacantes/paso-1`, que no matchea
      // nada en VACANTES_ROUTES y cae en el wildcard `**` -> `reclutador` -> LandingModule
      // (marketing público) — exactamente lo que D-10 rechazó. La forma correcta de colapsar el
      // subárbol legado completo a un único destino absoluto es un `children: [{path:'**', ...}]`:
      // el wildcard consume cualquier resto de segmentos y el redirect ABSOLUTO ('/reclutador/...')
      // ignora lo consumido en vez de concatenarlo.
      {
        path: 'onboarding',
        children: [{ path: '**', redirectTo: '/reclutador/vacantes' }],
      },
      {
        // Billing cutover preview: legacy buy entry points navigate here; land
        // them on the subscription plan page. Must precede `creditos` — the
        // router matches that prefix first and would never reach this redirect.
        path: 'creditos/comprar',
        redirectTo: 'plan',
        pathMatch: 'full',
      },
      {
        path: 'creditos',
        canActivate: [subscriptionGuard],
        loadChildren: () => import('@modules/credits/credits.module').then((m) => m.CreditsModule),
      },
      {
        // REQ-RSENT-030 / design D-5 — MUST NOT llevar ningún guard que pueda redirigir fuera de
        // esta ruta: es el destino de rebote de `subscriptionGuard` y el anti-bucle es estructural.
        path: 'plan',
        loadChildren: () => import('@modules/billing/billing.routes').then((m) => m.BILLING_ROUTES),
      },
      {
        path: 'vacantes',
        canActivate: [subscriptionGuard],
        loadChildren: () =>
          import('@modules/vacantes/vacantes.routes').then((m) => m.VACANTES_ROUTES),
      },
      {
        path: 'notificaciones',
        canActivate: [subscriptionGuard],
        loadChildren: () =>
          import('@modules/notifications/notifications.module').then(
            (m) => m.NotificationsModule
          ),
      },
      {
        path: 'solicitudes-entrevista',
        canActivate: [subscriptionGuard],
        loadChildren: () =>
          import(
            '@modules/video-interview-requests/video-interview-requests.routes'
          ).then((m) => m.VIDEO_INTERVIEW_REQUESTS_ROUTES),
      },
      REPORTES_ENTRY_ROUTE,
    ],
  },
  { path: '**', redirectTo: 'reclutador', pathMatch: 'full' },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'enabled',
    }),
  ],
  exports: [RouterModule],
  providers: [{ provide: UrlSerializer, useClass: OfertaLaboralUrlSerializer }],
})
export class AppRoutingModule {} // skipcq: JS-0327
